/**
 * Homepage FAQ content. Rendered as a visible, crawlable section (see
 * `src/components/faq/Faq.tsx`) **and** as `FAQPage` JSON-LD (via the `faq`
 * prop on `<Seo>`, wired from `src/pages/Home.tsx`) so both traditional
 * search engines and LLM/AI answer engines (ChatGPT, Gemini, Perplexity,
 * etc.) have a concise, structured, and quotable summary of who Brilliance
 * Solutions is, what it does, and how to reach it.
 *
 * Keep each `answer` self-contained (repeat the brand name instead of using
 * "we"/"our") — AI answer engines often quote a single answer in isolation,
 * without the surrounding question for context.
 *
 * Note: deliberately **not** typed against `./types`'s `FaqItem` here (the
 * shape still matches it structurally) — this module is imported directly
 * by `vite.config.ts` to build `llms.txt`, and `./types` also pulls in the
 * `@/lib/icons` path alias, which isn't resolvable outside the Vite/browser
 * build. See `FaqItem` in `src/content/types.ts` for the shared shape used
 * by `<Seo faq={...} />` and the FAQ components.
 */
export const faqContent = {
    sectionTitle: 'Frequently Asked Questions',
    items: [
        {
            question: 'What is Brilliance Solutions?',
            answer: 'Brilliance Solutions is a premium global meat import and distribution company headquartered in Riyadh, Saudi Arabia. It specializes in sourcing and distributing high-quality bovine (beef, veal, buffalo) and ovine (lamb, mutton, goat) products, alongside premium offal, to wholesalers, processors, manufacturers, and the hospitality sector across Saudi Arabia.',
        },
        {
            question: 'Where is Brilliance Solutions located?',
            answer: 'Brilliance Solutions is based in Riyadh, Saudi Arabia, and serves customers nationwide across the Kingdom of Saudi Arabia.',
        },
        {
            question: 'What products does Brilliance Solutions offer?',
            answer: 'Brilliance Solutions offers three core product lines: "Bovine Executive" (premium beef, tender veal, and high-protein buffalo), "Ovine Excellence" (lamb, mutton, and premium goat), and "Offal Excellence" (grass-fed beef liver, kidney, and heart).',
        },
        {
            question: 'Is Brilliance Solutions Halal-compliant?',
            answer: 'Yes. Halal integrity is a cornerstone of Brilliance Solutions. Every shipment is sourced through vetted, Halal-certified global partners and moves through a cold chain designed to guarantee uncompromised purity from pasture to table, in full compliance with Saudi SFDA regulations.',
        },
        {
            question: 'Which countries does Brilliance Solutions source meat from?',
            answer: 'Brilliance Solutions sources meat from a vetted global network spanning Oceania and Europe (Australia, New Zealand, Spain, and the Netherlands), the Americas (Brazil, Argentina, and Uruguay), and Africa and Asia (Kenya, South Africa, Ethiopia, Pakistan, and India).',
        },
        {
            question: 'Who does Brilliance Solutions serve?',
            answer: 'Brilliance Solutions serves manufacturers, food processors, wholesalers, and the hospitality sector throughout Saudi Arabia, providing a reliable, continuous supply of premium, quality-controlled meat products.',
        },
        {
            question: 'How can I contact Brilliance Solutions or request a quote?',
            answer: 'Brilliance Solutions can be reached through the contact form on its website, by emailing its general inquiry or sales team, or by phone. Visit the Contact page for the current email addresses and direct phone line.',
        },
        {
            question: 'What makes Brilliance Solutions different from other meat suppliers?',
            answer: 'Brilliance Solutions combines strict Halal integrity, full SFDA regulatory compliance, and advanced cold-chain logistics with real-time temperature tracking, backed by long-term partnerships with elite global suppliers rather than short-term, transactional sourcing.',
        },
    ],
};
